
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { namespace, Getter } from 'vuex-class';
import { EventDetails, EventRecord, ForceRestoreModalData } from '@/store/site-monitor/types';
import api from '../services/api.service';
import { UserPermissions } from '@/store/types';
import { Modules } from '@/store/ModuleTypes';
import { NotificationOptionsType } from '@/types/NotificationOptionsType';
import { ForceRestoreResponse } from '@/types/sv-data/responses/ForceRestoreResponse';
import { ForceRestoreRequest, ForceRestoreRequestBy } from '@/types/sv-data/requests/ForceRestoreRequest';

const SiteMonitor = namespace(Modules.SiteMonitor);

@Component({
})
export default class ForceRestore extends Vue {
	@Getter("getPermissions") permissions: UserPermissions;
	@Getter("getFeature") private getFeature: (featureName: string[]) => boolean;

	@SiteMonitor.Getter("getForceRestoreModalData") forceRestoreModalData!: ForceRestoreModalData;	
	@SiteMonitor.Getter("getEventDetails") eventDetails!: EventDetails

	@SiteMonitor.Mutation setForceRestoreModalData: (data: ForceRestoreModalData) => void;

    $refs!: {
        restoreModal: any;
    };

	private readonly maxLengthForReason: number = 5000;

    private restoralReason: string = "";
	private forceRestoreError: string | null = null;

	/** Life cycle hooks */

    private created(): void {
        this.resetData();
    }

	/** Watchers */
	@Watch("forceRestoreModalData.showModal")
    private onShowForceRestoreForAllAlarmsChanged(value: boolean): void {
        if (value) {
            this.restoralReason = "";
			this.forceRestoreError = null;
            this.$refs.restoreModal.show();
        } else {
            this.$refs.restoreModal.hide();
        }
    }

	/** Computed props */

	private get eventId(): number | null {
		return this.eventDetails ? this.eventDetails?.eventID : null;
	}
	
	private get isForceRestoreAlarmEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "ForceRestore"]) &&
		(this.getFeature(["Alarms", "SiteMonitor", "ForceRestore", "EnforceChecks"]) ? this.permissions.canForceRestoreAlarm : true);
	}

	private get isForceRestoreAllEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "ForceRestore", "AllAlarms"]) &&
			this.permissions.canForceRestoreAllAlarms;
	}

	private get enableOkButton(): boolean {
		return this.isModalDataValid &&  
			this.restoralReason.length > 0 &&
			this.restoralReason.length <= this.maxLengthForReason &&
			this.isForceRestoreAlarmEnabled;
	}

	private get forceRestoreEventRecord(): EventRecord | null {
		return this.forceRestoreModalData.eventRecordForRestore ?? null;
	}

	private get isForceRestoreForAllAlarms(): boolean {
		return this.forceRestoreModalData.restoreAllAlarms ?? false;
	}

	private get isModalDataValid(): boolean {
		if (this.forceRestoreModalData == null) {
			return false;
		}

		if (this.forceRestoreEventRecord != null && this.isForceRestoreForAllAlarms) {
			return false;
		}


		if (this.isForceRestoreForAllAlarms && (this.eventId == null || this.eventId <= 0)) {
			return false;
		}

		return true;
	}

	private get displayText(): string | null {
		const textPrefix: string =  "Are you sure you wish to force restore";

		if (!this.isForceRestoreForAllAlarms && this.forceRestoreEventRecord) {
			return `${textPrefix} '${this.forceRestoreEventRecord.details}' ?`;
		} else if (this.isForceRestoreForAllAlarms && this.eventId) {
			return `${textPrefix} ALL Alarms in the event?`;	
		}

		return null;
	}

	private get successMessageText(): string {
		const textPrefix: string =  "Successfully restored";

		if (!this.isForceRestoreForAllAlarms) {
			return `${textPrefix} alarm '${this.forceRestoreEventRecord.details}'`;
		}

		return `${textPrefix} ALL the Alarms in the event`;
	}

	private get modalTitle(): string {
		return this.isForceRestoreForAllAlarms ? `Force Restore All` : "Force Restore";
	}

	/** Methods */

    private async ok(): Promise<void> {
		let request: ForceRestoreRequest = {
			id: this.eventId,
			reason: this.restoralReason,
			requestBy: ForceRestoreRequestBy.User
		};

        if (this.restoralReason.length > 0) {
			let response: ForceRestoreResponse = null;

			if (this.isForceRestoreAllEnabled && this.forceRestoreModalData.restoreAllAlarms && !!this.eventId) {
				response = await api.forceRestoreAllAlarms(request);
			} else if (this.isForceRestoreAlarmEnabled &&  !!this.forceRestoreEventRecord) {
				// force restore single alarm
				request.id = this.forceRestoreEventRecord?.eventRecordID;

				response = await api.forceRestore(request);
			}

			if (response.success) {
				this.$notify({
					type: NotificationOptionsType.success,
					title: "Force Restore",
					text: this.successMessageText,
				});

				this.resetData();
			} else {
				this.forceRestoreError = response.error;

				this.$notify({
					type: NotificationOptionsType.error,
					title: "Force Restore Failed",
					text: response.error,
				});
			}
			
        }
    }

    private cancel(): void {
		this.resetData();
    }

	private resetData() : void {
		this.restoralReason = "";
		this.forceRestoreError = null;
		this.setForceRestoreModalData({ showModal: false, restoreAllAlarms: false, eventRecordForRestore: null});
	}
}
